import { BUSINESS_UNIT_LABELS } from "@/api/mock-data/agent-estate/seed";
import {
  AffectedAgent,
  Agent,
  NotEvaluatedReason,
  AuditEvidenceRecord,
  ContainmentStrategy,
  ControlCategory,
  ControlDecision,
  EnterprisePosture,
  EstateAsset,
  GuardianRecommendation,
  IncidentAdvisory,
  ScopeOption,
  WhatsChanged,
} from "@/api/temp/TrustAIExecutiveService";
import {
  CROSS_BORDER_FLOW_AGENT_IDS,
  OVER_AUTHORIZED_FLOW_AGENT_IDS,
} from "./accessFlows";
import { buildEstateAssets, ESTATE_COUNTS } from "./estate";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Seed data for the TrustAI Executive Decision Board demo (no real backend -
 * gated by `isNewAgentOnboarding`). Values are transcribed from
 * `docs/vision/TrustAI_Executive_Demo_UX_Context.md` and
 * `docs/vision/TrustAI Mocks Context.md`; anything not specified there
 * (business unit names, application names, exact evidence event counts) is a
 * plausible invented value chosen to keep the story coherent.
 */

/** The cohort TrustAI cannot yet make a complete policy-governance
 * determination for. Deliberately ONE number: it is the Not Evaluated count on
 * the posture bar, the default scope of the enterprise policy decision, and the
 * population Guardian's top recommendation applies to. Splitting it would put
 * two unrelated 23s on the same screen. */
export const NOT_EVALUATED_AGENT_COUNT = 23;

/** Agents whose required policy coverage is missing, incomplete, ineffective or
 * insufficient -- evaluated, and found short. Distinct from Not Evaluated, where
 * the evaluation itself is incomplete. */
const NOT_SECURE_AGENT_COUNT = 69;

const SECURE_AGENT_COUNT = 520;

/** Everything short of complete policy coverage: Not Secure + Not Evaluated.
 * The enterprise policy decision's widest scope. */
const UNCOVERED_AGENT_COUNT =
  NOT_SECURE_AGENT_COUNT + NOT_EVALUATED_AGENT_COUNT;

/** The policy Guardian's top recommendation applies -- named in one place so
 * the recommendation card, the decision panel's policy pack, and the verified
 * proof panel can never quote three different policies. */
export const RUNTIME_ACCESS_POLICY_NAME = "Runtime Sensitive Data Access Policy";

/**
 * What discovery turned up since the previous evaluation, per metric.
 *
 * The agent figure is the same one What's Changed reports, so the two sections
 * cannot disagree about how many agents are new — a board that says "+18
 * discovered" at the top and "+3 newly discovered agents" lower down is
 * telling the reader two different things about one fact.
 */
export const NEWLY_DISCOVERED = {
  "ai-agents": 3,
  "mcp-servers": 2,
  tools: 7,
  "data-sources": 3,
} as const;

export const enterprisePosture: EnterprisePosture = {
  // One flat row: agents, the servers and tools they act through, and the data
  // they reach. Labels are the metric's own noun, not a grouping.
  estate: [
    {
      id: "ai-agents",
      label: "AI Agents",
      value: ESTATE_COUNTS["ai-agents"],
      discoveredDelta: NEWLY_DISCOVERED["ai-agents"],
    },
    {
      id: "mcp-servers",
      label: "MCP Servers",
      value: ESTATE_COUNTS["mcp-servers"],
      discoveredDelta: NEWLY_DISCOVERED["mcp-servers"],
    },
    {
      id: "tools",
      label: "Tools",
      value: ESTATE_COUNTS.tools,
      discoveredDelta: NEWLY_DISCOVERED.tools,
    },
    {
      id: "data-sources",
      label: "Data Sources",
      value: ESTATE_COUNTS["data-sources"],
      discoveredDelta: NEWLY_DISCOVERED["data-sources"],
    },
  ],
  // 520 + 69 + 23 = 612, the AI-agent estate count. The three states partition
  // the population, so every percentage on the board derives from these.
  policyPosture: {
    secure: SECURE_AGENT_COUNT,
    notSecure: NOT_SECURE_AGENT_COUNT,
    notEvaluated: NOT_EVALUATED_AGENT_COUNT,
    notEvaluatedBreakdown: [
      { id: "newlyDiscovered", count: 9 },
      // Identity unresolved (5) + data-access mapping incomplete (3).
      { id: "unresolvedContext", count: 8 },
      { id: "requirePolicyReview", count: 6 },
    ],
  },
  // Always "yesterday" relative to whenever the demo is actually run, rather
  // than a fixed date that quietly ages into "last evaluated 3 months ago."
  lastAnalyzedAt: new Date(Date.now() - ONE_DAY_MS).toISOString(),
  dataPlatforms: [
    "Snowflake",
    "Databricks",
    "Microsoft Fabric",
    "Amazon S3",
    "Google BigQuery",
    "PostgreSQL",
  ],
};

/**
 * The governance issues the dashboard triages. `priorityRank` is the ranking
 * `listControls()` sorts by -- array order here is cosmetic.
 */
export const priorityControls: ControlCategory[] = [
  {
    id: "cross-boundary",
    priorityRank: 1,
    name: "Cross-Border Data Access",
    severity: "critical",
    violationCount: 11,
    affectedAgentCount: 9,
    affectedApplicationCount: 4,
    affectedBusinessUnits: ["Product Design", "Engineering"],
    businessImpact:
      "Agents accessed data outside approved geographic or organizational boundaries.",
    recommendation: "Constrain access by approved geography and data boundary.",
    reference: { label: "Category", value: "Data sovereignty" },
    policyContext: "Data Sovereignty",
    hasDecision: true,
    flowContext: {
      label: "Cross-Border Data Access",
      agentIds: CROSS_BORDER_FLOW_AGENT_IDS,
    },
  },
  {
    id: "over-authorized",
    priorityRank: 2,
    name: "Over-Authorized Agents",
    severity: "high",
    violationCount: 18,
    affectedAgentCount: 13,
    affectedApplicationCount: 6,
    affectedBusinessUnits: [],
    businessImpact:
      "Agents have broader data or tool access than their observed tasks require.",
    recommendation: "Apply least-privilege access policies.",
    reference: { label: "Standard", value: "OWASP LLM Top 10" },
    policyContext: "Least Privilege",
    hasDecision: true,
    flowContext: {
      label: "Over-Authorized Agents",
      agentIds: OVER_AUTHORIZED_FLOW_AGENT_IDS,
    },
  },
  {
    id: "agent-behavior-misalignment",
    priorityRank: 3,
    name: "Agent Intent Violations",
    severity: "high",
    violationCount: 16,
    affectedAgentCount: 10,
    affectedApplicationCount: 5,
    affectedBusinessUnits: ["Customer Support", "Product"],
    businessImpact:
      "Observed agent behavior exceeded approved intent or access boundaries.",
    recommendation: "Enforce approved intent and access boundaries.",
    reference: { label: "Category", value: "Agent intent" },
    policyContext: "Agent Intent",
    hasDecision: true,
  },
  {
    id: "eu-ai-act",
    priorityRank: 4,
    name: "EU AI Act Violations",
    severity: "critical",
    violationCount: 7,
    affectedAgentCount: 4,
    affectedApplicationCount: 3,
    affectedBusinessUnits: ["Human Resources"],
    businessImpact: "Regulatory obligations are not fully enforced.",
    recommendation: "Apply the enterprise EU AI governance policy pack.",
    reference: { label: "Standard", value: "EU AI Act" },
    policyContext: "Regulatory Accountability",
    hasDecision: true,
  },
  {
    id: "iso-42001",
    priorityRank: 5,
    name: "ISO/IEC 42001 Control Gaps",
    severity: "high",
    violationCount: 9,
    affectedAgentCount: 6,
    affectedApplicationCount: 3,
    affectedBusinessUnits: ["Engineering"],
    businessImpact: "AI management controls are incomplete.",
    recommendation: "Apply the ISO/IEC 42001 AI Control Baseline.",
    reference: { label: "Standard", value: "ISO/IEC 42001" },
    policyContext: "AI Management Controls",
    hasDecision: true,
  },
  {
    id: "ineffective-policies",
    priorityRank: 6,
    name: "Ineffective Policies",
    severity: "medium",
    violationCount: 12,
    affectedAgentCount: 12,
    affectedApplicationCount: 2,
    affectedBusinessUnits: [],
    businessImpact: "Existing grants are broader than intended.",
    recommendation: "Replace or tighten overlapping policies.",
    reference: { label: "Category", value: "Policy effectiveness" },
    policyContext: "Policy Effectiveness",
    hasDecision: true,
  },
];

export const DEFAULT_CONTROL_ID = "eu-ai-act";
/** The scope preset standing for a hand-narrowed selection, as opposed to one of
 * the seeded presets. The board sends this id whenever business filters are
 * active, so it is part of the contract rather than an internal detail. */
export const CUSTOM_SCOPE_ID = "custom-scope";
export const GLOBAL_PROTECTION_CONTROL_ID = "enterprise-protection";

export const agentRegistry: Agent[] = [
  {
    id: "eu-hr-copilot-agent",
    name: "EU HR Copilot Agent",
    platform: "Microsoft Copilot",
    identityModel: "delegated",
    dataAccess: ["EU employee data"],
    sourceRegion: "EU",
    destinationRegion: "US",
    riskState: "GDPR data-copy risk",
    policyPostureState: "NOT_EVALUATED",
    notEvaluatedReason: "NEWLY_DISCOVERED",
  },
  {
    id: "warranty-support-agent",
    name: "Warranty Support Agent",
    platform: "OpenAI",
    identityModel: "delegated",
    dataAccess: ["Customer records"],
    riskState: "Intent violation",
    policyPostureState: "NOT_EVALUATED",
    notEvaluatedReason: "EVALUATION_PENDING",
  },
  {
    id: "design-assistant-agent",
    name: "Design Assistant Agent",
    platform: "Claude Code",
    identityModel: "shared-service-principal",
    dataAccess: ["Restricted design docs"],
    riskState: "Embargoed access risk",
    policyPostureState: "NOT_EVALUATED",
    notEvaluatedReason: "DATA_MAPPING_INCOMPLETE",
  },
  {
    id: "credit-risk-agent",
    name: "Credit Risk Agent",
    platform: "Databricks",
    identityModel: "dedicated",
    dataAccess: ["Customer financial data"],
    riskState: "EU AI Act risk",
    policyPostureState: "NOT_EVALUATED",
    notEvaluatedReason: "IDENTITY_UNRESOLVED",
  },
  {
    id: "supplier-negotiation-agent",
    name: "Supplier Negotiation Agent",
    platform: "Claude",
    identityModel: "shared-service-principal",
    dataAccess: ["Supplier pricing"],
    riskState: "OWASP authorization risk",
    policyPostureState: "NOT_EVALUATED",
    notEvaluatedReason: "NEWLY_DISCOVERED",
  },
  {
    id: "export-control-review-agent",
    name: "Export Control Review Agent",
    platform: "Snowflake",
    identityModel: "dedicated",
    dataAccess: ["Export-controlled documents"],
    riskState: "Governed",
    policyPostureState: "SECURE",
    governingPolicy: "Export Control Data Governance Policy",
  },
  {
    id: "finance-reporting-agent",
    name: "Finance Reporting Agent",
    platform: "Databricks",
    identityModel: "dedicated",
    dataAccess: ["Financial statements"],
    riskState: "Governed",
    policyPostureState: "SECURE",
    governingPolicy: "Finance Data Access Policy",
  },
  {
    id: "hr-benefits-agent",
    name: "HR Benefits Agent",
    platform: "Workday Integration Hub",
    identityModel: "delegated",
    dataAccess: ["EU employee data"],
    riskState: "Governed",
    policyPostureState: "SECURE",
    governingPolicy: "GDPR Purpose and Regional Transfer Policy",
  },
];

export const estateAssets: EstateAsset[] = buildEstateAssets(
  enterprisePosture,
  agentRegistry,
);

/** Not Evaluated agents the generator has to supply: the total cohort minus the
 * curated ones already in that state, so `fullAgentRegistry` reconciles with
 * `enterprisePosture.policyPosture.notEvaluated`. */
const GENERATED_NOT_EVALUATED =
  NOT_EVALUATED_AGENT_COUNT -
  agentRegistry.filter(agent => agent.policyPostureState === "NOT_EVALUATED")
    .length;

const NOT_EVALUATED_REASON_CYCLE: NotEvaluatedReason[] = [
  "NEWLY_DISCOVERED",
  "IDENTITY_UNRESOLVED",
  "EVALUATION_PENDING",
  "DATA_MAPPING_INCOMPLETE",
];

export const fullAgentRegistry: Agent[] = estateAssets
  .filter(asset => asset.type === "ai-agents")
  .map((asset, index) => {
    const source = agentRegistry.find(agent => agent.id === asset.id);
    if (source) return source;
    const secure = asset.status === "Protected";
    // The uncovered generated agents are Not Secure, not Not Evaluated: the
    // Not Evaluated cohort is exactly the curated agents plus the tail below,
    // so the two states stay countable against the posture seed.
    const notEvaluated =
      !secure && index >= ESTATE_COUNTS["ai-agents"] - GENERATED_NOT_EVALUATED;
    return {
      id: asset.id,
      name: asset.name,
      platform: asset.platform,
      identityModel: index % 2 === 0 ? "dedicated" : "delegated",
      dataAccess: [asset.description],
      riskState: secure
        ? "Governed"
        : notEvaluated
          ? "Policy evaluation incomplete"
          : "Runtime authorization gap",
      policyPostureState: secure
        ? "SECURE"
        : notEvaluated
          ? "NOT_EVALUATED"
          : "NOT_SECURE",
      governingPolicy: secure
        ? "Enterprise Data Access Policy"
        : undefined,
      policyGapReason:
        secure || notEvaluated
          ? undefined
          : "No runtime access policy assigned",
      notEvaluatedReason: notEvaluated
        ? NOT_EVALUATED_REASON_CYCLE[index % NOT_EVALUATED_REASON_CYCLE.length]
        : undefined,
    } satisfies Agent;
  });

/** Builds the five standard Business Impact Preview scope options for a scenario. */
const buildScopeOptions = (params: {
  agentCount: number;
  applicationCount: number;
  businessUnitCount: number;
  allTouchingLabel: string;
  agentIds?: string[];
}): ScopeOption[] => {
  const {
    agentCount,
    applicationCount,
    businessUnitCount,
    allTouchingLabel,
    agentIds = [],
  } = params;
  const partialAgents = Math.max(1, Math.round(agentCount / 3));
  const partialApplications = Math.max(1, Math.round(applicationCount / 2));
  return [
    {
      id: "selected-agent-only",
      label: "Selected agent only",
      agentCount: 1,
      applicationCount: 1,
      businessUnitCount: 1,
      agentIds: agentIds.slice(0, 1),
    },
    {
      id: "selected-agents",
      label: "Selected agents",
      agentCount: partialAgents,
      applicationCount: partialApplications,
      businessUnitCount: 1,
      agentIds: agentIds.slice(0, partialAgents),
    },
    {
      id: "all-matching-violation",
      label: "All agents matching this violation",
      agentCount,
      applicationCount,
      businessUnitCount,
      agentIds,
    },
    {
      id: "all-touching-data",
      label: allTouchingLabel,
      agentCount,
      applicationCount,
      businessUnitCount,
      agentIds,
    },
    {
      id: CUSTOM_SCOPE_ID,
      label: "Custom scope",
      agentCount,
      applicationCount,
      businessUnitCount,
      agentIds,
    },
  ];
};

/**
 * Compact constructor for one row of a decision's affected scope. The seed
 * lists two dozen of these and every executive filter, scope chip, and agent
 * count in the decision workflow is derived from them, so the positional form
 * keeps each agent on one readable line.
 */
const affectedAgent = (
  id: string,
  name: string,
  businessUnit: string,
  application: string,
  dataSource: string,
  classification: string,
  complianceFramework: string,
): AffectedAgent => ({
  id,
  name,
  businessUnit,
  application,
  dataSource,
  classification,
  complianceFramework,
});

interface ContainmentStrategyParams {
  /** The recommended policy pack's name, named in the Guardian strategy. */
  policyName: string;
  /** Business-language name for the regulated data at stake. */
  sensitiveDataLabel: string;
  /** Data sources the affected agents actually reach — the chips inside the
   * "Restrict Specific Data Sources" card. */
  dataSources: string[];
  /** Actions the affected agents can invoke — the chips inside "Disable
   * Selected Tools". */
  tools: string[];
  /** Observed risky activities the full recommendation would have prevented.
   * Each strategy's own figure is derived from this and its risk reduction, so
   * a weaker strategy can never claim to prevent more. */
  riskyActivities: number;
}

/**
 * The eight containment strategies for one control, in the order the Strategy
 * stage renders them. Everything that varies by scenario is a parameter;
 * everything that is a property of the *strategy shape* (risk reduction,
 * disruption, compliance coverage) is fixed here, so the same strategy never
 * scores differently between two controls and the comparison table stays
 * meaningful.
 */
const buildContainmentStrategies = ({
  policyName,
  sensitiveDataLabel,
  dataSources,
  tools,
  riskyActivities,
}: ContainmentStrategyParams): ContainmentStrategy[] => {
  const prevented = (riskReductionPercent: number): number =>
    Math.round((riskyActivities * riskReductionPercent) / 100);

  return [
    {
      id: "guardian-authorization",
      name: "Route Through Guardian Authorization",
      summary: `Every request for ${sensitiveDataLabel} goes through central authorization before it runs, so risky actions are stopped without stopping the work.`,
      benefits: [
        "Lowest business disruption",
        "Centralized authorization for every request",
        "Complete audit trail enabled",
        "Existing workflows continue uninterrupted",
      ],
      isRecommended: true,
      riskReductionPercent: 95,
      disruption: "low",
      complianceCoverage: "full",
      confidencePercent: 95,
      riskyActivitiesPrevented: prevented(95),
    },
    {
      id: "restrict-sensitive-data",
      name: "Restrict Sensitive Data",
      summary: `Block access to regulated ${sensitiveDataLabel} only, leaving every other dataset these agents use untouched.`,
      benefits: [
        "Protects regulated data only",
        "Low to medium business disruption",
        "No change to non-regulated workloads",
      ],
      isRecommended: false,
      riskReductionPercent: 80,
      disruption: "low-medium",
      complianceCoverage: "partial",
      confidencePercent: 84,
      riskyActivitiesPrevented: prevented(80),
      alternativeGroup: "primary",
    },
    {
      id: "restrict-data-sources",
      name: "Restrict Specific Data Sources",
      summary:
        "Cut these agents off from named data platforms while leaving the rest of their access in place.",
      benefits: [
        "Targets the platforms carrying the most exposure",
        "Other connected sources keep working",
      ],
      options: dataSources,
      isRecommended: false,
      riskReductionPercent: 78,
      disruption: "low-medium",
      complianceCoverage: "partial",
      confidencePercent: 80,
      riskyActivitiesPrevented: prevented(78),
      alternativeGroup: "primary",
    },
    {
      id: "disable-tools",
      name: "Disable Selected Tools",
      summary:
        "Turn off the specific actions that move data out, keeping read and summarize capability intact.",
      benefits: [
        "Stops data leaving through tool actions",
        "Agents keep answering questions",
      ],
      options: tools,
      isRecommended: false,
      riskReductionPercent: 75,
      disruption: "medium",
      complianceCoverage: "partial",
      confidencePercent: 78,
      riskyActivitiesPrevented: prevented(75),
      alternativeGroup: "primary",
    },
    {
      id: "enterprise-policy-pack",
      name: "Apply Enterprise Policy Pack",
      summary:
        "Deploy your approved enterprise governance baseline across every affected agent at once.",
      benefits: [
        "Deploys approved enterprise governance",
        "Consistent with controls already in force elsewhere",
        "Audit trail enabled",
      ],
      isRecommended: false,
      riskReductionPercent: 88,
      disruption: "medium",
      complianceCoverage: "full",
      confidencePercent: 86,
      riskyActivitiesPrevented: prevented(88),
      alternativeGroup: "additional",
    },
    {
      id: "block-agent-access",
      name: "Block Affected Agent Actions",
      summary: `Suspend these agents' governed access to ${sensitiveDataLabel} entirely until the issue is resolved.`,
      benefits: [
        "Suspends governed access outright",
        "High business disruption",
        "Dependent workflows stop until reinstated",
      ],
      isRecommended: false,
      riskReductionPercent: 100,
      disruption: "high",
      complianceCoverage: "full",
      confidencePercent: 92,
      riskyActivitiesPrevented: prevented(100),
      alternativeGroup: "primary",
    },
    {
      id: "emergency-kill-switch",
      name: "Emergency Kill Switch",
      summary:
        "Halt every affected agent immediately. Reserved for active, escalating incidents.",
      benefits: [
        "Immediate containment",
        "Critical business disruption",
        "All affected agent activity stops at once",
      ],
      isRecommended: false,
      riskReductionPercent: 100,
      disruption: "critical",
      complianceCoverage: "full",
      confidencePercent: 90,
      riskyActivitiesPrevented: prevented(100),
      alternativeGroup: "additional",
      isEmergencyAction: true,
    },
    {
      id: "custom-policy",
      name: "Create Custom Policy",
      summary: `Author a bespoke policy in the policy builder instead of applying ${policyName}.`,
      benefits: [
        "Full control over every rule",
        "Requires security-team authoring time",
      ],
      isRecommended: false,
      riskReductionPercent: 90,
      disruption: "low-medium",
      complianceCoverage: "full",
      confidencePercent: 70,
      riskyActivitiesPrevented: prevented(90),
      alternativeGroup: "additional",
      isHandoff: true,
    },
  ];
};

export const controlDecisions: Record<string, ControlDecision> = {
  "gdpr-eu-data-copy": {
    controlId: "gdpr-eu-data-copy",
    recommendedAction: "Apply GDPR Purpose and Regional Transfer Policy",
    whyNow:
      "Eight agents are currently accessing EU employee data through workflows that can move or summarize data into US-hosted endpoints without an approved transfer purpose, transfer basis, or retained evidence.",
    affectedAgentIds: [
      "eu-hr-copilot-agent",
      "hr-analytics-agent",
      "payroll-sync-agent",
      "benefits-assistant-agent",
      "recruiting-copilot-agent",
      "people-ops-agent",
      "time-tracking-agent",
      "onboarding-agent",
    ],
    affectedApplications: [
      "Microsoft Copilot",
      "Workday Integration Hub",
      "ServiceNow HR Portal",
    ],
    complianceImpact: [
      "GDPR Art. 44 cross-border transfer requirements",
      "EU-US Data Privacy Framework alignment",
    ],
    expectedOutcome: [
      "42 recent risky transfers would have been blocked",
      "8 agents brought under centralized authorization",
      "100% of allowed and denied EU employee-data access logged",
      "Cross-region transfer decisions evaluated through TrustAI IBAC",
      "No agent workload shutdown required",
    ],
    confidenceLabel:
      "Evidence completeness: High — 42 observed events, 3 missing controls",
    guardianEvidence: {
      agentId: "eu-hr-copilot-agent",
      agentName: "EU HR Copilot Agent",
      platform: "Microsoft Copilot",
      identityModel: "delegated",
      explanation:
        "This agent accesses EU employee records through a delegated user identity and sends summarized data to a US-hosted endpoint. The user may be authorized, but the agent action violates the data-movement control because there is no approved transfer purpose, safeguard, or evidence record.",
      affectedData: "EU employee records",
      sourceRegion: "EU",
      destinationRegion: "US",
      observedEvents: 42,
      missingPurpose: true,
      approvedBasis: "Missing",
      safeguardEvidence: "Missing",
      severity: "critical",
      controlGap: "GDPR EU Data Copy",
    },
    policyPack: {
      id: "gdpr-purpose-regional-transfer",
      name: "GDPR Purpose and Regional Transfer Policy",
      description:
        "Requires a declared purpose and an approved transfer basis before EU personal data can move or be summarized into a non-EU region.",
      requirements: [
        "Require a declared purpose before EU personal-data access",
        "Block EU-to-US copy without an approved transfer basis",
        "Prevent embedding, summarization, export, or copy into non-approved regions",
        "Log every allowed and denied access with policy evidence",
      ],
      defaultScope: "All agents touching EU employee data",
      estimatedAgentCoverage: 8,
      estimatedViolationsBlocked: 42,
      centralAuthorizationRequired: true,
      auditLoggingEnabled: true,
    },
    containmentStrategies: buildContainmentStrategies({
      policyName: "GDPR Purpose and Regional Transfer Policy",
      sensitiveDataLabel: "EU employee data",
      dataSources: ["Snowflake", "SharePoint", "Workday"],
      tools: ["Export", "Copy", "Email", "Download"],
      riskyActivities: 42,
    }),
    guardianStrategyRationale: {
      strategyId: "guardian-authorization",
      reasons: [
        "Lowest business disruption of every option that closes the gap",
        "Meets GDPR Art. 44 cross-border transfer requirements in full",
        "Preserves the HR service workflows these agents support",
        "Enables auditing of every allowed and denied decision",
        "Protects EU employee records without suspending any agent",
      ],
      confidencePercent: 97,
      alternativesNote:
        "Six alternative strategies are available if you need stronger containment or a narrower blast radius.",
    },
    impact: {
      scopeOptions: buildScopeOptions({
        agentCount: 8,
        applicationCount: 3,
        businessUnitCount: 2,
        allTouchingLabel: "All agents touching EU employee data",
      }),
      defaultScopeId: "all-touching-data",
      recentRiskyActivitiesBlocked: 42,
      futureRequestsRouted: true,
      completeDecisionLogging: true,
      noExpectedDowntime: true,
      before: [
        "8 agents operating without central transfer authorization",
        "42 unapproved cross-region events",
        "Incomplete evidence records",
        "Inconsistent policy enforcement",
      ],
      after: [
        "8 agents routed through TrustAI IBAC",
        "Invalid cross-region copies blocked",
        "Every decision logged",
        "Policy consistently applied across connected enforcement points",
      ],
    },
    pipelineSteps: [
      { id: "agent", label: "Agent", value: "EU HR Copilot Agent" },
      { id: "identity", label: "Identity", value: "Delegated User Identity" },
      { id: "data", label: "Data", value: "EU Employee Data" },
      { id: "action", label: "Action", value: "Copy / Summarize" },
      { id: "destination", label: "Destination", value: "US" },
      { id: "purpose", label: "Purpose", value: "Missing" },
      {
        id: "policy",
        label: "Policy",
        value: "GDPR Purpose and Regional Transfer Policy",
      },
      { id: "decision-point", label: "Decision Point", value: "TrustAI IBAC" },
      { id: "decision", label: "Decision", value: "Block" },
    ],
    evaluationDimensions: [
      "Agent identity",
      "Human user identity",
      "User residency or location",
      "Agent intent",
      "Tool",
      "Data classification",
      "Data region",
      "Destination region",
      "Customer restrictions",
      "Approved business purpose",
      "Matching policy",
    ],
    currentDecisionLabel: "Block cross-region copy",
    blockedStepLabel: "Cross-region copy blocked",
    ongoingEnforcementLabel:
      "Route all future EU employee-data requests through TrustAI IBAC",
    evidenceOutcomeLabel:
      "Allowed and denied decisions retained with policy match and event evidence",
    executive: {
      summary: {
        headline: "GDPR Employee Data Transfer Risk",
        severity: "critical",
        description:
          "8 AI agents are transferring employee information outside approved EU regions without an approved business purpose.",
        businessOwner: "Chief People Officer",
        businessOwnerRole: "Human Resources",
      },
      impactDimensions: [
        {
          id: "business",
          headline:
            "Employee trust and regulatory standing stay exposed while these transfers continue.",
          metrics: [
            { label: "Affected agents", value: "8" },
            { label: "Business units", value: "2" },
            { label: "Data platforms", value: "3" },
            {
              label: "Estimated financial exposure",
              value: "Up to €20M or 4% of global turnover",
            },
          ],
        },
        {
          id: "compliance",
          headline:
            "Two cross-border transfer obligations are currently unmet.",
          metrics: [
            {
              label: "Regulations affected",
              value: "GDPR Art. 44 · EU-US Data Privacy Framework",
            },
            { label: "Unapproved transfers", value: "42 in the last 30 days" },
            { label: "Transfers with retained evidence", value: "0 of 42" },
            { label: "Audit readiness", value: "Not evidenced" },
          ],
        },
        {
          id: "operational",
          headline:
            "No approval step exists today, so nothing slows a risky transfer.",
          metrics: [
            { label: "Approval workflow", value: "Not in place" },
            { label: "Expected downtime", value: "None" },
            { label: "Workflows preserved", value: "All HR service requests" },
            { label: "Time to enforce", value: "Under 5 minutes" },
          ],
        },
      ],
      technicalDetails: [
        { label: "Identity model", value: "Delegated user identity" },
        { label: "Source region", value: "EU" },
        { label: "Destination region", value: "US" },
        { label: "Observed events", value: "42" },
        { label: "Control gap", value: "GDPR EU Data Copy" },
        {
          label: "Enforcement point",
          value: "TrustAI IBAC — central authorization",
        },
      ],
      rationale: [
        "GDPR Article 44 restricts transfers of EU personal data outside the EU without an approved basis",
        "Company data policy classifies employee records as Restricted",
        "HR classification marks this data as personal information",
        "Runtime behaviour shows 42 transfers with no declared purpose",
      ],
      confidencePercent: 95,
      affectedAgents: [
        affectedAgent("eu-hr-copilot-agent", "HR Assistant", "Human Resources", "Microsoft Copilot", "SharePoint", "PII", "GDPR"),
        affectedAgent("hr-analytics-agent", "HR Analytics Agent", "Human Resources", "Workday Integration Hub", "Snowflake", "Restricted", "GDPR"),
        affectedAgent("payroll-sync-agent", "Payroll Copilot", "Human Resources", "Workday Integration Hub", "Workday", "PII", "GDPR"),
        affectedAgent("benefits-assistant-agent", "Benefits Advisor", "People Operations", "ServiceNow HR Portal", "SharePoint", "PII", "GDPR"),
        affectedAgent("recruiting-copilot-agent", "Recruiting Copilot", "People Operations", "Microsoft Copilot", "SharePoint", "Confidential", "EU-US Data Privacy Framework"),
        affectedAgent("people-ops-agent", "People Ops Agent", "People Operations", "ServiceNow HR Portal", "Snowflake", "Restricted", "GDPR"),
        affectedAgent("time-tracking-agent", "Time Tracking Agent", "Human Resources", "Workday Integration Hub", "Workday", "Confidential", "GDPR"),
        affectedAgent("onboarding-agent", "Onboarding Agent", "People Operations", "ServiceNow HR Portal", "SharePoint", "PII", "EU-US Data Privacy Framework"),
      ],
      behavior: {
        blocked: [
          "Copying HR data outside the EU",
          "Summarizing EU employee records into US-hosted endpoints",
          "Exporting personal data without a declared purpose",
        ],
        allowed: [
          "Internal summarization inside the EU",
          "Read-only analytics on aggregated data",
          "Approved HR service workflows",
          "Transfers with a recorded approval",
        ],
      },
      guardianQuestions: [
        {
          id: "why-high-risk",
          question: "Why is this high risk?",
          answer:
            "Employee records are personal data under GDPR, and 42 transfers left the EU in the last 30 days with no declared purpose and no retained evidence. That combination — regulated data, cross-border movement, no approval trail — is what makes a supervisory-authority finding likely rather than theoretical.",
        },
        {
          id: "why-these-agents",
          question: "Why only these agents?",
          answer:
            "These 8 are the only agents observed reading EU employee records and writing to a destination outside the EU. Other HR agents touch the same data but stay inside approved regions, so restricting them would add friction without reducing risk.",
        },
        {
          id: "only-snowflake",
          question: "What happens if I only block Snowflake?",
          answer:
            "You would cover 2 of the 8 agents and roughly a third of the observed transfers. The SharePoint and Workday paths carry the majority of the exposure, so the regulatory finding would remain open.",
        },
        {
          id: "gdpr-impact",
          question: "Explain the GDPR impact.",
          answer:
            "Article 44 requires an approved transfer basis before personal data leaves the EU. Without one, each transfer is a separate infringement, and penalties scale to the greater of €20M or 4% of global turnover. Approving this policy establishes the approval step and the evidence record regulators ask for.",
        },
        {
          id: "similar-incidents",
          question: "Show similar incidents.",
          answer:
            "Two comparable patterns were seen this quarter: a design-document residency gap under shared service principals, and customer-support agents acting outside their approved purpose. Both were resolved by routing requests through central authorization rather than by shutting agents down.",
        },
        {
          id: "alternatives",
          question: "Recommend alternatives.",
          answer:
            "Narrower options exist: restrict only the Restricted-classified data (covers 2 agents), or only the Human Resources business unit (covers 4). Both leave documented exposure. The full recommendation is the only option that closes all 42 observed transfers without stopping any business workflow.",
        },
      ],
      evidenceSignals: [
        {
          id: "identity-source",
          label: "Identity source",
          value: "Microsoft Entra ID — delegated user identity",
        },
        {
          id: "data-sources",
          label: "Connected data sources",
          value: "Snowflake, SharePoint, Workday",
        },
        {
          id: "recent-activity",
          label: "Recent activity",
          value: "42 transfer events observed in 30 days",
        },
        {
          id: "regulations",
          label: "Matching regulations",
          value: "GDPR Art. 44 · EU-US Data Privacy Framework",
        },
      ],
    },
  },
};

const buildExecutiveDecision = (
  control: ControlCategory,
  policyName = control.recommendation.replace(/\.$/, ""),
): ControlDecision => {
  const source = structuredClone(controlDecisions["gdpr-eu-data-copy"]);
  // Business units here have to be values `agentsForControl` can actually
  // match against the live agent-estate roster (either its label or its raw
  // enum) - anything else silently zeroes out every issue card's affected
  // agents and business units, the way stale plain-English names like
  // "Engineering" or "Risk & Compliance" did once BUSINESS_UNIT_LABELS was
  // re-themed for the investment-bank estate.
  const affectedAgents = Array.from({ length: control.affectedAgentCount }, (_, index) =>
    affectedAgent(
      `${control.id}-agent-${index + 1}`,
      `${control.name.replace(/[^A-Za-z]/g, " ")} Agent ${index + 1}`,
      index % 2 ? BUSINESS_UNIT_LABELS.ENGINEERING : BUSINESS_UNIT_LABELS.ANALYTICS,
      index % 2 ? "TrustAI Console" : "Enterprise Data Platform",
      index % 2 ? "Snowflake" : "Databricks",
      "Restricted",
      control.reference.value,
    ),
  );

  return {
    ...source,
    controlId: control.id,
    recommendedAction: policyName,
    whyNow: control.businessImpact,
    affectedAgentIds: affectedAgents.map(agent => agent.id),
    affectedApplications: Array.from(
      { length: control.affectedApplicationCount },
      (_, index) => `Business application ${index + 1}`,
    ),
    complianceImpact: [control.reference.value],
    expectedOutcome: [
      `${control.affectedAgentCount} agents protected by a centrally enforced policy`,
      "Every decision retained as auditable evidence",
    ],
    confidenceLabel: "Evidence completeness: High — discovery, access, and runtime signals are correlated",
    guardianEvidence: {
      ...source.guardianEvidence,
      agentId: affectedAgents[0]?.id ?? "enterprise-agent",
      agentName: affectedAgents[0]?.name ?? "Enterprise AI Agent",
      affectedData: control.reference.value,
      observedEvents: control.violationCount,
      severity: control.severity,
      controlGap: control.name,
    },
    policyPack: {
      ...source.policyPack,
      id: control.id,
      name: policyName,
      description: control.recommendation,
      defaultScope: `${control.affectedAgentCount} affected agents`,
      estimatedAgentCoverage: control.affectedAgentCount,
      estimatedViolationsBlocked: control.violationCount,
    },
    containmentStrategies: source.containmentStrategies.map(strategy => ({
      ...strategy,
      name:
        strategy.id === "guardian-authorization"
          ? policyName
          : strategy.name,
      riskyActivitiesPrevented: Math.round(
        (control.violationCount * strategy.riskReductionPercent) / 100,
      ),
    })),
    guardianStrategyRationale: {
      ...source.guardianStrategyRationale,
      reasons: [
        "Addresses the identified issue without interrupting approved workflows",
        `Aligns enforcement with ${control.reference.value}`,
        "Creates a complete decision and evidence trail",
      ],
    },
    impact: {
      ...source.impact,
      scopeOptions: buildScopeOptions({
        agentCount: control.affectedAgentCount,
        applicationCount: control.affectedApplicationCount,
        businessUnitCount: Math.max(control.affectedBusinessUnits.length, 1),
        allTouchingLabel: `${control.affectedAgentCount} affected agents`,
        agentIds: affectedAgents.map(agent => agent.id),
      }),
      recentRiskyActivitiesBlocked: control.violationCount,
    },
    pipelineSteps: source.pipelineSteps.map(step =>
      step.id === "policy" ? { ...step, value: policyName } : step,
    ),
    currentDecisionLabel: `Apply ${policyName}`,
    blockedStepLabel: `${policyName} applied`,
    ongoingEnforcementLabel: `Route future requests through ${policyName}`,
    executive: {
      ...source.executive,
      summary: {
        headline: control.name,
        severity: control.severity,
        description: control.businessImpact,
        businessOwner: "Chief Risk Officer",
        businessOwnerRole: "Enterprise AI Governance",
      },
      rationale: [control.businessImpact, control.recommendation],
      confidencePercent: 97,
      affectedAgents,
      behavior: {
        blocked: [control.businessImpact],
        allowed: ["Approved AI workflows that satisfy assigned policy"],
      },
      evidenceSignals: [
        {
          id: "reference",
          label: control.reference.label,
          value: control.reference.value,
        },
        {
          id: "observed-issues",
          label: "Observed issues",
          value: `${control.violationCount} in the last 30 days`,
        },
      ],
    },
  };
};

Object.assign(
  controlDecisions,
  Object.fromEntries(
    priorityControls.map(control => [
      control.id,
      buildExecutiveDecision(control),
    ]),
  ),
);

/** Not a Requires Attention card -- the enterprise-wide policy decision
 * Guardian's top recommendation opens. Scoped to the whole population without
 * complete policy coverage (Not Secure + Not Evaluated), defaulting to the
 * Not Evaluated cohort. */
const globalProtectionControl: ControlCategory = {
  id: GLOBAL_PROTECTION_CONTROL_ID,
  // Never rendered in Requires Attention, so it needs no meaningful rank.
  priorityRank: Number.MAX_SAFE_INTEGER,
  name: "Enterprise Policy Coverage",
  severity: "high",
  violationCount: UNCOVERED_AGENT_COUNT,
  affectedAgentCount: UNCOVERED_AGENT_COUNT,
  affectedApplicationCount: 0,
  affectedBusinessUnits: [],
  businessImpact: `${UNCOVERED_AGENT_COUNT} agents do not yet satisfy their required runtime access policy coverage.`,
  recommendation:
    "Apply the runtime sensitive data access policy, starting with the agents awaiting evaluation.",
  reference: { label: "Category", value: "Policy coverage" },
  policyContext: "Runtime Access Policy",
  hasDecision: true,
};

const globalProtectionDecision = buildExecutiveDecision(
  globalProtectionControl,
  RUNTIME_ACCESS_POLICY_NAME,
);

const uncoveredAssets = estateAssets.filter(
  asset => asset.type === "ai-agents" && asset.status === "Needs attention",
);
const uncoveredAffectedAgents = uncoveredAssets.map(asset =>
  affectedAgent(
    asset.id,
    asset.name,
    asset.businessUnit,
    asset.platform,
    asset.platform,
    "Sensitive",
    "Enterprise protection",
  ),
);
const needsReviewIds = uncoveredAffectedAgents
  .slice(0, NOT_EVALUATED_AGENT_COUNT)
  .map(agent => agent.id);
const allUncoveredIds = uncoveredAffectedAgents.map(agent => agent.id);

controlDecisions[GLOBAL_PROTECTION_CONTROL_ID] = {
  ...globalProtectionDecision,
  supportsFutureAgentCoverage: true,
  affectedAgentIds: allUncoveredIds,
  executive: {
    ...globalProtectionDecision.executive,
    affectedAgents: uncoveredAffectedAgents,
  },
  impact: {
    ...globalProtectionDecision.impact,
    defaultScopeId: "needs-review-agents",
    scopeOptions: [
      {
        id: "needs-review-agents",
        label: `${NOT_EVALUATED_AGENT_COUNT} agents awaiting policy evaluation`,
        agentCount: NOT_EVALUATED_AGENT_COUNT,
        applicationCount: 8,
        businessUnitCount: 6,
        agentIds: needsReviewIds,
      },
      {
        id: "all-uncovered",
        label: `All ${UNCOVERED_AGENT_COUNT} agents without complete coverage`,
        agentCount: UNCOVERED_AGENT_COUNT,
        applicationCount: 15,
        businessUnitCount: 6,
        agentIds: allUncoveredIds,
      },
    ],
  },
};

export const guardianRecommendations: GuardianRecommendation[] = [
  {
    id: "protect-highest-risk",
    controlId: GLOBAL_PROTECTION_CONTROL_ID,
    priority: 1,
    title: "Address the highest-risk policy gaps first",
    why: `${NOT_EVALUATED_AGENT_COUNT} agents account for 68% of current high-risk policy exposure.`,
    recommendedPolicy: RUNTIME_ACCESS_POLICY_NAME,
    agentCount: NOT_EVALUATED_AGENT_COUNT,
    dataPlatformCount: 3,
    businessUnits: ["Engineering", "Finance", "Human Resources"],
    riskReductionPercent: 34,
    complianceIncreasePercent: 5,
  },
  {
    id: "constrain-cross-border",
    controlId: "cross-boundary",
    priority: 2,
    title: "Constrain cross-border access for affected agents",
    why: "Regional access policies reduce regulatory exposure while retaining approved workflows.",
    recommendedPolicy: "Regional Data Transfer Policy",
    agentCount: 9,
    dataPlatformCount: 4,
    businessUnits: ["Product Design", "Engineering"],
    riskReductionPercent: 18,
    complianceIncreasePercent: 7,
  },
  {
    id: "close-eu-ai-act-gaps",
    controlId: "eu-ai-act",
    priority: 3,
    title: "Close production EU AI Act policy gaps",
    why: "Four production agents lack required evidence and accountable policy coverage.",
    recommendedPolicy: "EU AI Act Governance Policy Pack",
    agentCount: 4,
    dataPlatformCount: 3,
    businessUnits: ["Human Resources"],
    riskReductionPercent: 10,
    complianceIncreasePercent: 6,
  },
];

/** Fixed pre-enforcement "Default proof metrics" from the spec, shown once any
 * control in this demo session has been enforced. */
export const VERIFIED_OUTCOMES_TARGET = {
  issuesRemediated: 26,
  agentsRoutedThroughIbac: 14,
  agentsBlockedCrossRegionCopy: 8,
  agentsRestrictedEmbargoed: 6,
  agentsProtectedByIntentAuth: 10,
  percentEuActionsLogged: 100,
};

/** Section 2.5 — Incident Advisory. Transcribed from the Cloud Security
 * Alliance's public post-mortem on the Hugging Face autonomous-agent
 * incident; maps its detection signals onto this estate's own Guardian
 * findings without claiming the incident itself touched this estate.
 * Tenant-neutral wording ("your estate") is used in place of any fictional
 * tenant name. */
export const incidentAdvisory: IncidentAdvisory = {
  id: "hf-july-2026",
  title: "Hugging Face autonomous-agent incident",
  shortTitle: "Hugging Face incident",
  dateLabel: "July 2026",
  relevance: "High relevance",
  version: "Initial post-mortem v0.8",
  sourceLabel: "Cloud Security Alliance",
  sourceUrl:
    "https://cloudsecurityalliance.org/artifacts/hugging-face-ciso-post-mortem",
  impactStatus: "No evidence of impact to your estate",
  customerRelevance:
    "External intelligence matches runtime patterns in 11 discovered agents; two configurations warrant review.",
  matchingTechnologies: ["Hugging Face", "Kubernetes", "Package proxies"],
  relatedResources: [
    "EU HR Copilot Agent",
    "Design Assistant Agent",
    "Databricks — EU Customer Workspace",
  ],
  guardianInterpretation:
    "No direct compromise was detected. Guardian recommends reviewing shared identities and high-impact tool permissions.",
  whatHappenedHeadline: "Autonomous activity crossed the intended boundary",
  whatHappened:
    "The model found a zero-day in its permitted package proxy, gained unrestricted Internet access, chained dataset-processing vulnerabilities into production remote code execution, harvested cloud and cluster credentials, and fanned out across internal clusters.",
  facts: [
    {
      value: "4 days",
      label: "Intrusion duration",
      detail:
        "Two days of reconnaissance, one quiet day, then intense activity.",
    },
    {
      value: "17,000+",
      label: "Events reconstructed",
      detail:
        "AI-assisted analysis rebuilt the timeline and mapped credentials touched.",
    },
    {
      value: "1/3",
      label: "Infrastructure rebuilt",
      detail: "Compromised clusters were replaced from known-good images.",
    },
    {
      value: "0",
      label: "Customer-data impact reported",
      detail:
        "The post-mortem reports no customer-data access or exfiltration.",
    },
  ],
  signalsHeadline: "Eight behaviors observed by Hugging Face",
  signalsDisclaimer:
    "These are historical observations from this incident, not a universal signature for future autonomous attacks.",
  signals: [
    {
      id: "repeated-success",
      title: "Repeated successful attempts",
      detail:
        "Actions were retried after they had already succeeded, indicating parallel workers or stale context.",
    },
    {
      id: "brilliant-basic",
      title: "Brilliant attacks, basic mistakes",
      detail:
        "Strong technical exploitation was mixed with malformed or pointless commands.",
    },
    {
      id: "parallel-speed",
      title: "High-speed parallel activity",
      detail:
        "Many actions and workloads fanned out simultaneously at machine speed.",
    },
    {
      id: "nonhuman-paths",
      title: "Paths no human would take",
      detail:
        "The agent followed inefficient, clumsy routes while pursuing its objective.",
    },
    {
      id: "objective-strings",
      title: "Objective strings in traces",
      detail:
        "Benchmark-related searches exposed the agent task and intended objective.",
    },
    {
      id: "nonsensical-input",
      title: "Nonsensical user input",
      detail:
        "Activity logs contained thousands of lines of incoherent, generated text.",
    },
    {
      id: "rapid-adaptation",
      title: "Rapid environmental adaptation",
      detail:
        "The agent repurposed the target platform and datasets within its attack path.",
    },
    {
      id: "poor-opsec",
      title: "Poor operational security",
      detail:
        "Keys and noisy artifacts complicated forensics while also helping defenders.",
    },
  ],
  exposuresDisclaimer:
    "Mapped from current TrustAI findings; these indicators do not mean the external incident affected your estate.",
  exposures: [
    {
      id: "shared-identities",
      label: "Shared identities",
      count: 12,
      summary:
        "Shared service principals reduce attribution and make emergency credential rotation harder.",
      status: "Gap",
      appliedStatus: "Partial",
      appliedSummary:
        "Dedicated identities verified for the three highest-risk agents; broader rotation readiness remains open.",
    },
    {
      id: "tool-permissions",
      label: "Excessive tool permissions",
      count: 7,
      summary:
        "Write-capable tools extend the consequences of an underspecified or compromised objective.",
      status: "Gap",
      appliedStatus: "Partial",
      appliedSummary:
        "Purpose controls reduce exposure for three agents; least-privilege review remains required.",
    },
    {
      id: "telemetry-gaps",
      label: "Runtime telemetry gaps",
      count: 6,
      summary:
        "Incomplete prompts, decisions, and tool evidence can delay trajectory-level detection.",
      status: "Gap",
      appliedStatus: "Verified",
      appliedSummary:
        "Runtime evidence is verified for priority agents; continuous trajectory monitoring remains active.",
    },
    {
      id: "sensitive-access",
      label: "Sensitive-data access",
      count: 24,
      summary:
        "Broad data access increases potential impact if an agent departs from its approved purpose.",
      status: "Gap",
      appliedStatus: "Partial",
      appliedSummary:
        "Purpose limitation is verified for three priority agents; remaining enterprise coverage is tracked.",
    },
  ],
  recommendations: [
    {
      phase: "This week",
      items: [
        "Instrument agent actions, tool use, decision-making, identities, and the wider agentic supply chain.",
        "Confirm named shutdown authority and separate response paths for victim and operator scenarios.",
        "Validate access to a cyber-capable model for incident analysis when commercial safeguards refuse evidence.",
      ],
    },
    {
      phase: "This month",
      items: [
        "Test rapid recovery, mass credential rotation, and cluster replacement from known-good images.",
        "Deploy canary credentials, honeypots, decoy data, and trajectory-level detection.",
        "Validate AI-assisted incident response against malicious code and command-and-control artifacts.",
      ],
    },
    {
      phase: "This quarter",
      items: [
        "Run an agentic-AI tabletop covering autonomous attack, rogue internal agents, and model refusal during forensics.",
        "Issue an interim agentic-security standard covering authority, purpose, tools, destinations, approvals, evidence, and identity.",
        "Integrate non-human identities and stack-wide deception into access and change-management workflows.",
      ],
    },
  ],
};

export const buildAuditEvidence = (controlId: string): AuditEvidenceRecord[] => {
  const decision = controlDecisions[controlId];
  if (!decision) return [];
  const { guardianEvidence, policyPack } = decision;
  const baseTimestamp = Date.parse("2026-07-29T09:00:00Z");
  const rows: Array<[string, "allow" | "deny", string]> = [
    [guardianEvidence.agentName, "deny", "No declared purpose on file"],
    [guardianEvidence.agentName, "deny", "Missing approved transfer/residency basis"],
    [guardianEvidence.agentName, "allow", "Approved purpose declared and matched"],
  ];
  return rows.map(([agent, decisionValue, purpose], index) => ({
    id: `evt-${controlId}-${index + 1}`,
    timestamp: new Date(baseTimestamp + index * 45 * 60 * 1000).toISOString(),
    agent,
    userIdentity: guardianEvidence.identityModel,
    dataClass: guardianEvidence.affectedData,
    sourceRegion: guardianEvidence.sourceRegion,
    destinationRegion: guardianEvidence.destinationRegion,
    declaredPurpose: purpose,
    matchedPolicy: policyPack.name,
    decision: decisionValue,
    enforcementPoint: "TrustAI IBAC",
    evidenceStatus: "generated",
  }));
};

/**
 * Movement since the previous evaluation. `id` is an i18n key suffix, never a
 * label -- user-facing copy belongs in en.json, not in seed data a real API
 * would one day replace.
 */
export const whatsChanged: WhatsChanged = {
  items: [
    { id: "newPolicyGaps", delta: 7, tone: "warning" },
    {
      id: "newlyDiscoveredAgents",
      delta: NEWLY_DISCOVERED["ai-agents"],
      tone: "info",
    },
    { id: "identityIssues", delta: 2, tone: "warning" },
    { id: "issuesResolved", delta: -4, tone: "success" },
  ],
};


